'use client';

import { type ComponentProps, useEffect, useId, useState } from 'react';

import { tv } from '../../lib/tv';
import { Label } from '../ui/label';
import { Slider as SliderPrimitive } from '../ui/slider';
import { useFieldControl, useFieldLabelId } from './field';
import { tooltipStyles } from './tooltip-styles';

type PrimitiveProps = ComponentProps<typeof SliderPrimitive>;

export type SliderProps = Omit<
  PrimitiveProps,
  'orientation' | 'getThumbProps' | 'renderThumbContent' | 'children'
> & {
  /**
   * Figma "Type" follows the number of values: `[60]` is Single, `[17, 46]`
   * is Range (two thumbs).
   */
  value?: number[];
  defaultValue?: number[];
  /** How a value reads - in the dragging tooltip and to screen readers (`aria-valuetext`). */
  formatValue?: (value: number) => string;
  /** Accessible names of the Range thumbs, appended to the slider's name. Default Minimum / Maximum. */
  thumbLabels?: [start: string, end: string];
  /** Show the value in a Tooltip above the thumb while dragging (Figma State=Dragging). Default true. */
  showTooltip?: boolean;
};

const valueTooltip = tv({
  slots: {
    root: 'pointer-events-none absolute bottom-2 left-0 z-10 hidden -translate-x-1/2 flex-col items-center group-data-dragging/thumb:flex',
  },
});

/**
 * The Figma Tooltip (Placement=Top, Length=Short) drawn in place above the
 * thumb - same body and arrow classes as Tooltip, but not portalled, so it
 * follows the thumb while dragging. Decorative: the value is announced via
 * `aria-valuetext`.
 */
function ValueTooltip({ text }: { text: string }) {
  const styles = tooltipStyles();
  return (
    <span data-slot="slider-tooltip" aria-hidden className={valueTooltip().root()}>
      <span
        data-slot="tooltip-content"
        className={styles.content({ className: 'animate-none whitespace-nowrap' })}
      >
        {text}
      </span>
      <svg
        data-slot="tooltip-arrow"
        width={12}
        height={6}
        viewBox="0 0 12 6"
        className={styles.arrow({ className: 'shrink-0' })}
      >
        <polygon points="0,0 12,0 6,6" />
      </svg>
    </span>
  );
}

/**
 * Pick a value (one thumb) or a range (two) on a continuum - variation
 * strength, duration, volume (Figma: Components ↳ Slider & Stepper ↳ Slider).
 * Radix Slider: ←/→ step, Shift+arrow or PageUp/PageDown ×10, Home/End.
 * Always pair it with a visible value - use SliderField, or a Field.
 * Inside a Field, the Field label names the thumbs.
 */
export function Slider({
  formatValue = String,
  thumbLabels = ['Minimum', 'Maximum'],
  showTooltip = true,
  value,
  defaultValue,
  min = 0,
  max = 100,
  onValueChange,
  onPointerDown,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  ...props
}: SliderProps) {
  const uid = useId();
  const fieldLabelId = useFieldLabelId();
  const {
    id,
    'aria-describedby': describedBy,
    'aria-invalid': invalid,
    ...rest
  } = useFieldControl(props) as typeof props & {
    'aria-describedby'?: string;
    'aria-invalid'?: boolean;
  };
  const [internal, setInternal] = useState(defaultValue ?? [min]);
  const values = value ?? internal;
  const range = values.length > 1;
  const [dragging, setDragging] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!dragging) return;
    const stop = () => setDragging(false);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    return () => {
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
  }, [dragging]);

  // The slider's name: aria-labelledby, the Field label, or aria-label (held
  // in a hidden span so Range thumbs can append "Minimum" / "Maximum").
  const nameId =
    ariaLabelledBy ?? fieldLabelId ?? (ariaLabel ? `${uid}-name` : undefined);

  return (
    <>
      <SliderPrimitive
        value={value}
        defaultValue={defaultValue}
        min={min}
        max={max}
        onValueChange={(next) => {
          setInternal(next);
          onValueChange?.(next);
        }}
        onPointerDown={(event) => {
          onPointerDown?.(event);
          if (!rest.disabled) setDragging(true);
        }}
        getThumbProps={(index) => ({
          id: index === 0 ? id : undefined,
          'aria-labelledby': range
            ? [nameId, `${uid}-thumb-${index}`].filter(Boolean).join(' ')
            : nameId,
          'aria-valuetext':
            values[index] === undefined ? undefined : formatValue(values[index]),
          'aria-describedby': describedBy,
          'aria-invalid': invalid,
          'data-dragging': (dragging && active === index) || undefined,
          onFocus: () => setActive(index),
        })}
        renderThumbContent={(index) => (
          <>
            {range && (
              <span id={`${uid}-thumb-${index}`} hidden>
                {thumbLabels[index]}
              </span>
            )}
            {showTooltip && values[index] !== undefined && (
              <ValueTooltip text={formatValue(values[index])} />
            )}
          </>
        )}
        {...rest}
      />
      {ariaLabel && !ariaLabelledBy && !fieldLabelId && (
        <span id={`${uid}-name`} hidden>
          {ariaLabel}
        </span>
      )}
    </>
  );
}

export type SliderFieldProps = SliderProps & {
  /** Figma "Label" - Label/Medium, names the slider. */
  label: string;
  /** Word or number under the start of the track. Default: `formatValue(min)`. */
  minLabel?: string;
  /** Word or number under the end of the track. Default: `formatValue(max)`. */
  maxLabel?: string;
  /** Class for the outer box (width, margins). */
  className?: string;
};

const sliderField = tv({
  slots: {
    root: 'flex w-full flex-col gap-2.5',
    top: 'flex items-center',
    label:
      'text-label-md text-text-primary flex-1 group-data-disabled/slider-field:text-text-disabled',
    value: 'text-data-md text-text-secondary tabular-nums',
    anchors: 'text-caption text-text-tertiary flex justify-between',
  },
});

/**
 * Slider with its label, the live value (Data/Medium) and min / max anchors
 * (Figma: Components ↳ Slider & Stepper ↳ Slider Field). Use words for the
 * anchors when the numbers mean little (Subtle → Bold).
 */
export function SliderField({
  label,
  minLabel,
  maxLabel,
  formatValue = String,
  className,
  value,
  defaultValue,
  min = 0,
  max = 100,
  onValueChange,
  ...props
}: SliderFieldProps) {
  const id = useId();
  const [internal, setInternal] = useState(defaultValue ?? [min]);
  const values = value ?? internal;
  const styles = sliderField();
  const disabled = Boolean(props.disabled);
  return (
    <div
      data-slot="slider-field"
      data-disabled={disabled || undefined}
      className={styles.root({ className: ['group/slider-field', className] })}
    >
      <div className={styles.top()}>
        <Label
          id={`${id}-label`}
          htmlFor={`${id}-thumb`}
          data-disabled={disabled || undefined}
          className={styles.label()}
        >
          {label}
        </Label>
        {/* The live value; screen readers get it from each thumb's aria-valuetext. */}
        <output
          htmlFor={`${id}-thumb`}
          aria-hidden
          data-slot="slider-field-value"
          data-disabled={disabled || undefined}
          className={styles.value()}
        >
          {values.map(formatValue).join(' – ')}
        </output>
      </div>
      <Slider
        id={`${id}-thumb`}
        aria-labelledby={`${id}-label`}
        value={value}
        defaultValue={defaultValue}
        min={min}
        max={max}
        formatValue={formatValue}
        onValueChange={(next) => {
          setInternal(next);
          onValueChange?.(next);
        }}
        {...props}
      />
      <div
        data-slot="slider-field-anchors"
        className={styles.anchors()}
        aria-hidden
        data-disabled={disabled || undefined}
      >
        <span>{minLabel ?? formatValue(min)}</span>
        <span>{maxLabel ?? formatValue(max)}</span>
      </div>
    </div>
  );
}
